import { isEmptyString } from '@/utils/string';
import type { Customer } from './entity';

/**
 * Customer display derivations — how a profile is labelled in the UI (name → identifier fallback).
 * Kept in the `customer` entity so any feature showing a profile derives the label the same way.
 */

const FIRST_NAME_KEYS = ['first_name', 'firstname', 'first name', 'first'];
const LAST_NAME_KEYS = ['last_name', 'lastname', 'last name', 'last'];
const FULL_NAME_KEYS = ['full_name', 'fullname', 'full name', 'name'];

function firstAttr(attrs: Record<string, string> | undefined, keys: string[]): string | null {
  if (attrs == null) return null;
  for (const key of keys) {
    const value = attrs[key];
    if (!isEmptyString(value ?? '')) return value;
  }
  return null;
}

/**
 * A customer's name from its attributes: "first last" → a full-name attribute → null.
 * @public
 */
export function customerName(attributes: Record<string, string>): string | null {
  const first = firstAttr(attributes, FIRST_NAME_KEYS);
  const last = firstAttr(attributes, LAST_NAME_KEYS);
  if (first != null && last != null) return `${first} ${last}`;
  return firstAttr(attributes, FULL_NAME_KEYS);
}

/** Fallback identifier label when there's no name: email → phone → "{term} with id {id}". */
function customerIdentifierDisplay(customer: Customer, personSingular: string): string {
  const { email: identifierEmail, phone, id } = customer.identifiers ?? {};
  // Ember reads email from `attrs.email` (the customer attributes); fall back to the identifiers
  // email so an address that lives on either source still shows.
  const email = customer.attributes?.email ?? identifierEmail ?? '';
  if (!isEmptyString(email)) return email;
  if (!isEmptyString(phone ?? '')) return phone;
  return `${personSingular} with id ${id ?? customer.id}`;
}

/**
 * Display label for a customer: first/last name → a full-name attribute → email → phone →
 * "{term} with id {id}". `personSingular` is the workspace's people term (e.g. "Person").
 * @public
 */
export function customerDisplay(customer: Customer, personSingular: string): string {
  return customerName(customer.attributes ?? {}) ?? customerIdentifierDisplay(customer, personSingular);
}
